import { isEqual } from 'underscore';
import { sanitizeScene, applyAnimations } from '@studio/program';
import { BROADCAST_SET_PROGRAM } from 'actions/Types';
import { withCurrentParams } from 'utils/actionUtils';
import { addSignedUrlsToBroadcast } from '@shoflo/studio-redux/utils';

/**
 * @param scene object the scene to set the program to
 * @param isTransient boolean whether we should save this to the API or not, if isTransient is true, persistence is not desired.
 * @param otherConfig Object containing other configs
 */
function setProgram(scene, isTransient, otherConfig = { emitSocket: true }) {
  return withCurrentParams(
    (dispatch, { teamId, eventId, broadcastId }, getState) => {
      const { program, overlays, backgrounds, sceneLayers } = getState();

      const { updatedSceneLayers } = otherConfig;
      /**
       * There is an instance where there is no change to a scene,
       * we shouldn't update the program because it causes undesired change to broadcast sources
       */
      if (isEqual(scene, program)) return;

      const action = {
        type: BROADCAST_SET_PROGRAM,
        payload: applyAnimations(program, scene),
        sceneLayerSources: { overlays, backgrounds },
        request: async (apiClient) => {
          //Deep clone, because this appears to mutate the parameter
          const scenePayload = sanitizeScene(JSON.parse(JSON.stringify(scene)));
          let broadcast = await apiClient.broadcast.updateProgram(
            teamId,
            eventId,
            broadcastId,
            scenePayload
          );

          /**
           * API only returns ` overlays, backgrounds, globalSceneLayers`
           * only when we update program from scene.
           * If these parameters are not returned, we get them from state.
           */
          if (!broadcast.overlays) {
            broadcast = {
              ...broadcast,
              overlays: overlays,
            };
          }

          if (!broadcast.backgrounds) {
            broadcast = {
              ...broadcast,
              backgrounds: backgrounds,
            };
          }

          if (!broadcast.globalSceneLayers) {
            broadcast.globalSceneLayers = updatedSceneLayers ?? sceneLayers;
          }

          // Assigning broadcast._id since it is not returned in the response of the broadcast.updateProgram API, ensuring it is available for the further update call
          broadcast._id = broadcastId;
          const broadcastWithSignedUrls = await addSignedUrlsToBroadcast(
            { ...broadcast, team: teamId, event: eventId },
            apiClient,
            teamId,
            eventId
          );

          return broadcastWithSignedUrls;
        },
        isTransient,
        otherConfig,
      };
      // If it's transient, don't save to API...
      if (isTransient || action.isTransient) delete action.request;

      dispatch(action);
    }
  );
}

export default setProgram;
